import React, { useState } from "react";
import { AlertCircle, Check, ChevronsUpDown, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FlagChip } from "./FlagChip";
import { formatRelativeTime } from "@/lib/match/formatters";

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDot(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

// ── Vendor-record (source_row) lookup ────────────────────────────────────────
// source_row carries the provider CSV's RAW HEADERS ("Account Name", "Street",
// "Partner Account"…) while field paths end in Contract H segments ("name",
// "street", "advisor_company"). The old exact-match lookup
// (source[lastSegment]) therefore NEVER hit and the whole VENDOR RECORD
// column rendered "—". Resolve via (1) per-field header aliases matched
// against normalized headers, then (2) the normalized last segment itself.

function normKey(k) {
  return String(k || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Contract H last-segment → known provider-report header spellings.
// Nextiva canonical headers first; add per-provider variants as they appear.
const SOURCE_ALIASES = {
  name:                      ["account name", "customer name", "customer"],
  street:                    ["street", "address", "service address", "address 1"],
  city:                      ["city"],
  state:                     ["state", "st"],
  zip:                       ["zip", "zipcode", "zip code", "postal code"],
  advisor_company:           ["partner account", "advisor company", "partner"],
  sales_rep:                 ["sales rep", "rep"],
  mrgmv:                     ["mrr", "mrc", "monthly revenue"],
  nrc:                       ["nrc", "one time charge"],
  commission_ban:            ["account number", "ban", "billing account number"],
  provider_order_id:         ["provider sales order id", "provider order id"],
  contract_term:             ["term", "contract term"],
  contract_signed:           ["created date", "contract signed"],
  contract_start:            ["close date", "contract start"],
  order_description:         ["opportunity name", "order description"],
  provider_revenue_category: ["order type"],
  provider:                  ["provider", "carrier"],
  product:                   ["product"],
};

function resolveSourceValue(source, fieldPath) {
  if (!source || typeof source !== "object") return null;
  const lastKey = fieldPath.split(".").pop();
  const byNorm = {};
  for (const k of Object.keys(source)) byNorm[normKey(k)] = source[k];
  for (const alias of SOURCE_ALIASES[lastKey] || []) {
    const v = byNorm[normKey(alias)];
    if (v != null && v !== "") return v;
  }
  const direct = byNorm[normKey(lastKey)];
  return direct != null && direct !== "" ? direct : null;
}

function fmtMoney(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function prettyValue(v, isMoney) {
  if (v == null || v === "") return <span className="text-boss-text-muted">—</span>;
  if (isMoney) return <span className="font-mono">{fmtMoney(v)}</span>;
  return String(v);
}

// Fields that must be filled for AppDirect to accept a new order.
const CRITICAL_FOR_NEW_ORDER = new Set([
  "order_details.provider_order_id",
  "order_details.contract_type",
  "order_details.mrgmv",
  "order_details.contract_start",
]);

// Map a flag to the field paths it's relevant to (mirrors POC logic).
function flagsForField(path, allFlags) {
  const flags = allFlags || [];
  const relevant = [];
  if (flags.includes("name_variation") && path.includes("customer.name")) relevant.push("name_variation");
  if (flags.includes("address_discrepancy") && path.includes("location.")) relevant.push("address_discrepancy");
  if (flags.includes("new_customer") && path.includes("customer.")) relevant.push("new_customer");
  if (flags.includes("new_vs_existing_discrepancy") && path.includes("contract_type")) relevant.push("new_vs_existing_discrepancy");
  return relevant;
}

function valuesEqual(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

// Spec §3.3 row tinting — compares vendor vs initial INTERNAL MATCH value.
// Returns a CSS class string (or inline-style equivalent via data-attr).
function computeRowTone(srcVal, initialRight, fieldFlags, derivedOnly) {
  const srcEmpty = srcVal == null || srcVal === "";
  if (srcEmpty || derivedOnly) return "neutral";
  if (valuesEqual(srcVal, initialRight)) return "match";
  if (fieldFlags.includes("name_variation")) return "partial";
  if (fieldFlags.includes("address_discrepancy")) return "diff";
  // Substring containment heuristic — soft amber instead of hard red.
  if (initialRight != null && initialRight !== "") {
    const a = String(srcVal).trim().toLowerCase();
    const b = String(initialRight).trim().toLowerCase();
    if (a.length > 3 && b.length > 3 && (a.includes(b) || b.includes(a))) return "partial";
  }
  return "diff";
}

// Tone → left-border color using BOSS CSS variables.
const TONE_BORDER = {
  match:   "border-l-[var(--boss-success)]",
  partial: "border-l-[var(--boss-warning)]",
  diff:    "border-l-[var(--boss-danger)]",
  neutral: "border-l-transparent",
};

// ── DropdownInput ─────────────────────────────────────────────────────────────

function DropdownInput({ field, cur, onChange, testId, changedCls }) {
  const opts = field.options || [];
  const allowCustom = !!field.allowCustom;
  const valueIsCustom = cur != null && cur !== "" && !opts.includes(cur);
  const [customMode, setCustomMode] = useState(allowCustom && valueIsCustom);

  if (allowCustom && customMode) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={cur ?? ""}
          onChange={(e) => onChange(field.path, e.target.value)}
          placeholder="Type custom value…"
          className={`h-8 text-[13px] font-mono boss-input ${changedCls}`}
          data-testid={testId}
          autoFocus
        />
        <button
          type="button"
          onClick={() => { onChange(field.path, ""); setCustomMode(false); }}
          className="text-[10px] uppercase tracking-wider text-boss-text-muted hover:text-boss-text whitespace-nowrap"
          data-testid={`${testId}-use-list`}
        >
          Use list
        </button>
      </div>
    );
  }

  return (
    <Select
      value={cur ?? ""}
      onValueChange={(val) => {
        if (val === "__custom__") {
          setCustomMode(true);
          onChange(field.path, "");
        } else {
          onChange(field.path, val);
        }
      }}
    >
      <SelectTrigger
        className={`h-8 text-[13px] font-mono boss-input ${changedCls}`}
        data-testid={testId}
      >
        <SelectValue placeholder="Select…" />
      </SelectTrigger>
      {/* max-h via min(): cn() is twMerge(clsx(...)) and className merges LAST,
          so a bare max-h-[280px] lands in the same conflict group as the
          primitive's max-h-[--radix-select-content-available-height] and DELETES
          it — the list then renders past the viewport edge on a short window and
          the bottom options are unreachable. min() keeps both limits. */}
      <SelectContent className="max-h-[min(280px,var(--radix-select-content-available-height))] overflow-y-auto">
        {opts.map((opt) => (
          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
        ))}
        {allowCustom && (
          <SelectItem value="__custom__" data-testid={`${testId}-custom-option`}>
            Custom…
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

// ── ProductCombobox ───────────────────────────────────────────────────────────
// Renders a Command-in-Popover searchable list of provider products.
// Options are from provider_products (≤200 items) — filtered client-side.
// The AI-preselected product name is shown with a confidence badge.

const METHOD_LABEL = {
  llm:             "AI matched",
  prefilter_exact: "exact match",
  alias:           "learned alias",
};

function confidenceBadgeCls(score) {
  if (score >= 80) return "bg-[var(--boss-success-soft)] text-[var(--boss-success-text)] border-[var(--boss-success-border)]";
  if (score >= 50) return "bg-[var(--boss-warning-soft)] text-[var(--boss-warning-text)] border-[var(--boss-warning-border)]";
  return "bg-[var(--boss-danger-soft)] text-[var(--boss-danger-text)] border-[var(--boss-danger-border)]";
}

function ProductCombobox({
  curName,
  providerProducts,
  productMatch,
  changedCls,
  onChange,
  onProductSelect,
  testId,
  catalogWarning = null,
  productsFetchedAt = null,
  onRefreshProducts,
  refreshingProducts = false,
}) {
  const [open, setOpen] = useState(false);

  const selectedProduct = providerProducts.find((p) => p.name === curName) || null;
  // Report text that isn't an actual option must not read as a selection —
  // live 2026-07-10 a Magic-Mapped opportunity note ("6/30/2026, JARA IB,
  // called to add a license") sat in the trigger looking chosen.
  const unmatchedText = Boolean(curName) && !selectedProduct && providerProducts.length > 0;

  function handleSelect(product) {
    onChange(product.name);
    if (onProductSelect) onProductSelect(product.id, product.name);
    setOpen(false);
  }

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={testId}
            className={`h-8 w-full flex items-center justify-between rounded-md border px-3 text-[13px] font-mono text-left boss-input ${changedCls} ${!curName ? "text-boss-text-muted" : ""} ${unmatchedText ? "border-[var(--boss-warning-border)] text-[var(--boss-warning-text)] italic" : ""}`}
          >
            <span className="truncate">{curName || "Search products…"}</span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-boss-text-muted" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0 bg-boss-surface border border-boss-border text-boss-text"
          align="start"
        >
          <Command>
            <CommandInput
              placeholder="Search products…"
              className="h-9 text-[13px] text-boss-text bg-boss-surface"
            />
            <CommandList>
              <CommandEmpty className="py-4 text-center text-[13px] text-boss-text-muted">
                No products found.
              </CommandEmpty>
              <CommandGroup>
                {providerProducts.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={p.name}
                    onSelect={() => handleSelect(p)}
                    className="text-[13px] text-boss-text data-[selected=true]:bg-boss-elevated"
                  >
                    <Check
                      className={`mr-2 h-3.5 w-3.5 ${selectedProduct?.id === p.id ? "opacity-100" : "opacity-0"}`}
                    />
                    {p.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Confidence badge from AI product_match */}
      {productMatch && productMatch.product_name && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${confidenceBadgeCls(productMatch.confidence ?? 0)}`}
            title={productMatch.reasoning || ""}
          >
            {productMatch.confidence ?? 0}% confidence
          </span>
          <span className="text-[11px] text-boss-text-muted italic">
            {METHOD_LABEL[productMatch.method] || productMatch.method || "AI matched"}
          </span>
        </div>
      )}
      {unmatchedText && (
        <p className="text-[11px] text-[var(--boss-warning-text)] italic">
          Report text — not an AppDirect product. Pick one from the list before booking.
        </p>
      )}
      {catalogWarning ? (
        <p className="text-[11px] text-[var(--boss-warning-text)] italic" data-testid={`${testId}-catalog-warning`}>
          {catalogWarning}
        </p>
      ) : (!providerProducts || providerProducts.length === 0) && (
        <p className="text-[11px] text-boss-text-muted italic">No products available for this provider.</p>
      )}

      {/* Fetched-at + refresh — provider_products is frozen at session-trigger
          time; this is the recovery path that doesn't require re-running the
          whole session. formatRelativeTime returns null when never fetched,
          so the line is simply omitted rather than showing a fake "never". */}
      {(formatRelativeTime(productsFetchedAt) || onRefreshProducts) && (
        <div className="flex items-center justify-between gap-2">
          {formatRelativeTime(productsFetchedAt) && (
            <span className="text-[10.5px] text-boss-text-muted">
              Catalog fetched {formatRelativeTime(productsFetchedAt)}
            </span>
          )}
          {onRefreshProducts && (
            <button
              type="button"
              onClick={onRefreshProducts}
              disabled={refreshingProducts}
              className="inline-flex items-center gap-1 text-[10.5px] text-boss-text-muted hover:text-boss-text disabled:opacity-50 disabled:cursor-wait"
              data-testid={`${testId}-refresh-catalog`}
            >
              <RefreshCw className={`h-2.5 w-2.5 ${refreshingProducts ? "animate-spin" : ""}`} />
              {refreshingProducts ? "Refreshing…" : "Refresh product catalog"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── SalesRepCombobox ──────────────────────────────────────────────────────────
// Renders a Command-in-Popover searchable list of advisor sales reps.
// Shows rep name with email as subtext. Falls back to plain text input when
// no reps are found for the advisor.

function SalesRepCombobox({ curName, salesReps, changedCls, onChange, onSalesRepSelect, testId }) {
  const [open, setOpen] = useState(false);

  const items    = salesReps?.items || [];
  const advisor  = salesReps?.advisor || null;
  const loading  = salesReps === null; // null means not yet fetched
  const noReps   = !loading && items.length === 0;

  const selectedRep = items.find((r) => r.name === curName) || null;

  function handleSelect(rep) {
    onChange(rep.name);
    if (onSalesRepSelect) onSalesRepSelect(rep.id, rep.name);
    setOpen(false);
  }

  // Degrade to free-text when no reps are available for this advisor
  if (noReps) {
    return (
      <div className="space-y-1">
        <Input
          value={curName ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={`h-8 text-[13px] font-mono boss-input ${changedCls}`}
          data-testid={testId}
          placeholder="Enter sales rep name…"
        />
        <p className="text-[11px] text-boss-text-muted italic">
          No reps found for{advisor ? ` ${advisor.name}` : " this advisor"} — enter manually.
        </p>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          disabled={loading}
          className={`h-8 w-full flex items-center justify-between rounded-md border px-3 text-[13px] font-mono text-left boss-input ${changedCls} ${!curName ? "text-boss-text-muted" : ""} ${loading ? "opacity-60 cursor-wait" : ""}`}
        >
          <span className="truncate">{loading ? "Loading reps…" : (curName || "Search reps…")}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-boss-text-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0 bg-boss-surface border border-boss-border text-boss-text"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder="Search reps…"
            className="h-9 text-[13px] text-boss-text bg-boss-surface"
          />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-[13px] text-boss-text-muted">
              No reps found.
            </CommandEmpty>
            <CommandGroup>
              {items.map((rep) => (
                <CommandItem
                  key={rep.id}
                  value={rep.name}
                  onSelect={() => handleSelect(rep)}
                  className="text-[13px] text-boss-text data-[selected=true]:bg-boss-elevated flex flex-col items-start gap-0.5 py-2"
                >
                  <div className="flex items-center gap-2">
                    <Check
                      className={`h-3.5 w-3.5 shrink-0 ${selectedRep?.id === rep.id ? "opacity-100" : "opacity-0"}`}
                    />
                    <span>{rep.name}</span>
                  </div>
                  {rep.email && (
                    <span className="pl-5 text-[11px] text-boss-text-muted leading-tight">{rep.email}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── FieldInput ────────────────────────────────────────────────────────────────

function FieldInput({ field, cur, orig, changed, onChange }) {
  const testId = `row-detail-derived-input-${field.path}`;
  const changedCls = changed ? "border-[var(--boss-info)]" : "";

  if (field.type === "textarea") {
    return (
      <Textarea
        value={cur ?? ""}
        onChange={(e) => onChange(field.path, e.target.value)}
        className={`min-h-[64px] text-[13px] font-mono boss-input ${changedCls}`}
        data-testid={testId}
      />
    );
  }

  if (field.type === "select") {
    return (
      <DropdownInput
        field={field}
        cur={cur}
        onChange={onChange}
        testId={testId}
        changedCls={changedCls}
      />
    );
  }

  if (field.type === "date") {
    return (
      <input
        type="date"
        value={cur ?? ""}
        onChange={(e) => onChange(field.path, e.target.value)}
        className={`h-8 w-full rounded-md border border-boss-border bg-boss-base px-3 text-[13px] font-mono text-boss-text shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-boss-accent ${changedCls}`}
        data-testid={testId}
      />
    );
  }

  if (field.type === "boolean") {
    return (
      <div className="flex items-center gap-2 py-1">
        <Checkbox
          id={testId}
          checked={!!cur}
          onCheckedChange={(checked) => onChange(field.path, checked)}
          data-testid={testId}
        />
        <label htmlFor={testId} className="text-[13px] cursor-pointer select-none text-boss-text">
          {cur ? "Yes" : "No"}
        </label>
      </div>
    );
  }

  if (field.type === "percentage") {
    return (
      <div className="relative flex items-center">
        <Input
          type="number"
          value={cur ?? ""}
          onChange={(e) => onChange(field.path, e.target.value !== "" ? Number(e.target.value) : "")}
          className={`h-8 text-[13px] font-mono boss-input pr-6 ${changedCls}`}
          min={0}
          max={100}
          step={0.01}
          data-testid={testId}
        />
        <span className="absolute right-2 text-[12px] text-boss-text-muted pointer-events-none">%</span>
      </div>
    );
  }

  // Default: text or money
  return (
    <Input
      value={cur ?? ""}
      onChange={(e) =>
        onChange(field.path, field.type === "money" && e.target.value !== "" ? Number(e.target.value) : e.target.value)
      }
      className={`h-8 text-[13px] font-mono boss-input ${changedCls}`}
      data-testid={testId}
    />
  );
}

// ── FieldEvidence ─────────────────────────────────────────────────────────────

function FieldEvidence({ path, flags }) {
  const relevant = flagsForField(path, flags);
  if (relevant.length === 0) {
    return <span className="text-[12px] text-boss-text-muted">No flags</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {relevant.map((f) => (
        <FlagChip key={f} flag={f} />
      ))}
    </div>
  );
}

// ── FieldRow (exported) ───────────────────────────────────────────────────────

/**
 * FieldRow — one row inside FieldGrid.
 *
 * Renders the three-column layout:
 *   VENDOR RECORD (read-only source value) |
 *   GOING TO APPDIRECT (editable internal match) |
 *   EVIDENCE/FLAGS (per-field flags — hidden when showEvidenceColumn=false)
 *
 * Props:
 *   field              — field definition from FIELDS_BY_TAB
 *   source             — row.source_row (raw provider CSV row)
 *   edited             — local edited copy of output
 *   original           — row.output (read-only, used for diff indicators)
 *   flags              — row.audit_metadata.flags (string[])
 *   showEvidenceColumn — boolean
 *   colsTemplate       — CSS grid-template-columns string
 *   onChange           — (dotPath: string, value: any) => void
 *   providerProducts   — [{id, name}] — used when field.type === "product_combobox"
 *   productMatch       — {product_id, product_name, confidence, method} | null
 *   salesReps          — {advisor, items} | null — used when field.type === "sales_rep_combobox"
 *   onProductSelect    — (id, name) => void
 *   onSalesRepSelect   — (id, name) => void
 *   catalogWarning     — string | null — truthful reason the product catalog couldn't load
 *   productsFetchedAt  — string | null — ISO-8601 UTC of the last successful catalog fetch
 *   onRefreshProducts  — () => void — re-fetches the catalog for this row's session
 *   refreshingProducts — boolean — disables the refresh action while in flight
 */
export function FieldRow({
  field,
  source,
  edited,
  original,
  flags,
  showEvidenceColumn,
  colsTemplate,
  onChange,
  providerProducts = [],
  productMatch = null,
  salesReps = null,
  onProductSelect,
  onSalesRepSelect,
  catalogWarning = null,
  productsFetchedAt = null,
  onRefreshProducts,
  refreshingProducts = false,
}) {
  // Resolve source value from the source_row — alias-aware, header-normalized
  // (see resolveSourceValue above; the old exact-match on the path's last
  // segment never hit the raw CSV headers and rendered "—" everywhere).
  const srcVal = resolveSourceValue(source, field.path);
  const isMoney = field.type === "money";

  const orig = getDot(original, field.path);
  const cur = getDot(edited, field.path);
  const changed = JSON.stringify(orig) !== JSON.stringify(cur);

  const fieldFlags = flagsForField(field.path, flags);
  const sourceIsEmpty = srcVal == null || srcVal === "";

  const tone = computeRowTone(srcVal, orig, fieldFlags, !!field.derivedOnly);

  return (
    <div
      className={`grid border-t border-boss-border min-h-[48px] border-l-2 ${TONE_BORDER[tone]}`}
      style={{ gridTemplateColumns: colsTemplate }}
      data-match-state={tone}
      data-testid={`field-row-${field.path}`}
    >
      {/* VENDOR RECORD (left, read-only) */}
      <div className="px-4 py-3.5 text-[13px] border-r border-boss-border">
        <div className="text-[10px] uppercase tracking-wider text-boss-text-muted mb-0.5 font-semibold">
          {field.label}
        </div>
        <div className="font-mono text-boss-text" data-testid={`row-detail-source-value-${field.path}`}>
          {sourceIsEmpty && field.derivedOnly ? (
            <span className="italic text-boss-text-muted text-[12.5px] leading-snug">
              {/* "(derived field)" answered nothing — say where the value
                  actually comes from (per-field hint in contractH.js) */}
              {field.derivedHint
                ? `— ${field.derivedHint} —`
                : '— no report column; filled by BOSS —'}
            </span>
          ) : (
            prettyValue(srcVal, isMoney)
          )}
        </div>
      </div>

      {/* GOING TO APPDIRECT (middle, editable) */}
      <div
        className={`px-4 py-3.5 ${showEvidenceColumn ? "border-r border-boss-border" : ""}`}
        data-testid={`row-detail-internal-match-${field.path}`}
      >
        <div className="text-[10px] uppercase tracking-wider text-boss-text-muted mb-0.5 font-semibold">
          {field.label}
        </div>

        {field.type === "product_combobox" ? (
          <ProductCombobox
            curName={cur ?? ""}
            providerProducts={providerProducts}
            productMatch={productMatch}
            changedCls={changed ? "border-[var(--boss-info)]" : ""}
            onChange={(name) => onChange(field.path, name)}
            onProductSelect={onProductSelect}
            testId={`row-detail-derived-input-${field.path}`}
            catalogWarning={catalogWarning}
            productsFetchedAt={productsFetchedAt}
            onRefreshProducts={onRefreshProducts}
            refreshingProducts={refreshingProducts}
          />
        ) : field.type === "sales_rep_combobox" ? (
          <SalesRepCombobox
            curName={cur ?? ""}
            salesReps={salesReps}
            changedCls={changed ? "border-[var(--boss-info)]" : ""}
            onChange={(name) => onChange(field.path, name)}
            onSalesRepSelect={onSalesRepSelect}
            testId={`row-detail-derived-input-${field.path}`}
          />
        ) : (
          <FieldInput
            field={field}
            cur={cur}
            orig={orig}
            changed={changed}
            onChange={onChange}
          />
        )}

        {changed && field.type !== "product_combobox" && field.type !== "sales_rep_combobox" && (
          <div className="mt-1 text-[11px] text-[var(--boss-info)]">
            Edited (was: {prettyValue(orig, isMoney)})
          </div>
        )}
        {changed && (field.type === "product_combobox" || field.type === "sales_rep_combobox") && (
          <div className="mt-1 text-[11px] text-[var(--boss-info)]">
            Edited (was: {prettyValue(orig, false)})
          </div>
        )}
        {CRITICAL_FOR_NEW_ORDER.has(field.path) && (cur == null || cur === "") && (
          <div
            className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-[var(--boss-warning)]/40 bg-[var(--boss-warning-soft)] px-1.5 py-0.5 text-[10.5px] uppercase tracking-wider text-[var(--boss-warning)]"
            data-testid={`row-detail-required-chip-${field.path}`}
          >
            <AlertCircle className="h-3 w-3" />
            Required for new order
          </div>
        )}
      </div>

      {/* EVIDENCE/FLAGS (right, narrow, read-only) */}
      {showEvidenceColumn && (
        <div className="px-4 py-3.5" data-testid={`row-detail-evidence-${field.path}`}>
          <div className="text-[10px] uppercase tracking-wider text-boss-text-muted mb-0.5 font-semibold">
            &nbsp;
          </div>
          <FieldEvidence path={field.path} flags={flags} />
        </div>
      )}
    </div>
  );
}
